import { tempoApi } from '@/lib/api/tempoApi';
import type {
  BulkTeamActionRequest,
  BulkTeamActionResponse,
  CreateTeamRequest,
  ImportTeamsRequest,
  ImportTeamsSearch,
  ImportTeamsSearchParameters,
  ImportTeamsSearchResponse,
  MembershipBulkAddRequest,
  MembershipBulkDeleteRequest,
  MembershipBulkUpdateRequest,
  ResourceFilterStatus,
  ResourceTeamMembership,
  SyncStatusResponse,
  SyncTeamsRequest,
  Team,
  TeamResponse,
  TeamsLookupRequest,
  TeamsLookupResponse,
  TeamsResponse,
  TeamWithIncludedResponse,
} from '@/lib/api/types/teams';

const getTeams = async (): Promise<TeamsResponse> => {
  return await tempoApi().get('api/v1/capacity/teams').json<TeamsResponse>();
};

const getTeam = async (
  teamId: number,
  resourceStatus?: ResourceFilterStatus,
): Promise<TeamWithIncludedResponse> => {
  const params = new URLSearchParams({ includeMembers: 'true' });
  if (resourceStatus) {
    params.append('resourceStatus', resourceStatus);
  }
  return await tempoApi()
    .get(`api/v1/capacity/teams/${teamId}?${params.toString()}`)
    .json<TeamWithIncludedResponse>();
};

const updateTeam = async ({
  teamId,
  data,
}: {
  teamId: number;
  data: Partial<CreateTeamRequest>;
}): Promise<Team> => {
  const response = await tempoApi()
    .patch(`api/v1/capacity/teams/${teamId}`, {
      json: data,
    })
    .json<TeamResponse>();
  return response.data;
};

const deleteTeam = async (teamId: number): Promise<void> => {
  await tempoApi().delete(`api/v1/capacity/teams/${teamId}`);
};

const createTeam = async (data: CreateTeamRequest): Promise<Team> => {
  return await tempoApi().post('api/v1/capacity/teams', { json: data }).json<Team>();
};

const bulkAddMembersToTeam = async (teamId: number, resourceIds: number[]): Promise<Team> => {
  const response = await tempoApi()
    .post(`api/v1/capacity/team-memberships/team/${teamId}/bulk`, { json: { resourceIds } })
    .json<{ data: Team }>();
  return response.data;
};

const getImportTeamsSearch = async ({
  sourceType,
  credentialsId,
  instanceId,
}: ImportTeamsSearchParameters): Promise<ImportTeamsSearch[]> => {
  const params = new URLSearchParams({
    sourceType,
    credentialsId: String(credentialsId),
    instanceId,
  });
  const response = await tempoApi()
    .get(`api/v1/capacity/import/teams/search?${params.toString()}`)
    .json<ImportTeamsSearchResponse>();
  return response.items;
};

const importTeams = async (data: ImportTeamsRequest) => {
  return await tempoApi().post('api/v1/capacity/import/teams', { json: data }).json();
};

const syncTeam = async (teamId: number) => {
  return await tempoApi().post(`api/v1/capacity/teams/${teamId}/sync`).json();
};

const syncTeams = async (data: SyncTeamsRequest) => {
  return await tempoApi().post('api/v1/capacity/teams/sync', { json: data }).json();
};

const getSyncStatus = async () => {
  return await tempoApi().get('api/v1/capacity/sync/status').json<SyncStatusResponse>();
};

const lookupTeams = async (data: TeamsLookupRequest) => {
  return await tempoApi()
    .post('api/v1/capacity/teams/lookup', {
      json: { ...data, expand: ['MEMBERSHIPS', 'CAPACITY'] },
    })
    .json<TeamsLookupResponse>();
};

const getResourceTeamMemberships = async (
  resourceId: number,
): Promise<ResourceTeamMembership[]> => {
  return await tempoApi()
    .get(`api/v1/capacity/team-memberships/resource/${resourceId}`)
    .json<ResourceTeamMembership[]>();
};

const deleteTeamMembership = async (membershipId: number): Promise<void> => {
  await tempoApi().delete(`api/v1/capacity/team-memberships/${membershipId}`);
};

const bulkDeleteTeamMemberships = async (data: MembershipBulkDeleteRequest): Promise<void> => {
  await tempoApi().delete('api/v1/capacity/team-memberships/bulk', {
    json: data,
  });
};

const bulkUpdateTeamMemberships = async (data: MembershipBulkUpdateRequest): Promise<void> => {
  await tempoApi().put('api/v1/capacity/team-memberships/bulk', {
    json: data,
  });
};

const bulkAddMemberships = async (
  resourceId: number,
  data: MembershipBulkAddRequest,
): Promise<ResourceTeamMembership[]> => {
  return await tempoApi()
    .post(`api/v1/capacity/team-memberships/resource/${resourceId}/bulk`, { json: data })
    .json<ResourceTeamMembership[]>();
};

const bulkTeamActions = async (data: BulkTeamActionRequest): Promise<BulkTeamActionResponse> => {
  return await tempoApi()
    .post('api/v1/capacity/team-memberships/bulk-actions', { json: data })
    .json<BulkTeamActionResponse>();
};

export const TeamsApi = {
  getTeam,
  getTeams,
  updateTeam,
  deleteTeam,
  createTeam,
  bulkAddMembersToTeam,
  bulkAddMemberships,
  bulkTeamActions,
  getResourceTeamMemberships,
  bulkUpdateTeamMemberships,
  bulkDeleteTeamMemberships,
  deleteTeamMembership,
  getImportTeamsSearch,
  importTeams,
  syncTeam,
  syncTeams,
  getSyncStatus,
  lookupTeams,
} as const;
